/**
 * §2.1 Home の動的化用ヘルパ。
 *
 * - timeOfDay: 朝/昼/夕/夜 を時刻から判定し、背景グラデーション + BGM トーンを返す
 * - pickHomeMessage: 過去ドロー・未読的な状況を反映した巫女のセリフを生成
 *
 * BGM は Web Audio 合成 (Track C 録音待ち) のため、useSound に
 * "ambient_morning" / "ambient_day" / "ambient_dusk" / "ambient_night" を渡す想定。
 * 既存の useSound に追加しないと再生できないので、placeholder として "tone" を返す形で
 * AudioContext で穏やかなドローン音を鳴らせるよう Home 側で組む。
 */

import type { StoredDraw } from "./storage";
import { RARITY_DISPLAY } from "./omikuji";

export type TimeOfDay = "morning" | "day" | "dusk" | "night";

export function getTimeOfDay(d: Date = new Date()): TimeOfDay {
  const h = d.getHours();
  if (h >= 5 && h < 10) return "morning";
  if (h >= 10 && h < 16) return "day";
  if (h >= 16 && h < 19) return "dusk";
  return "night";
}

export type MoodTheme = {
  /** 背景 (radial + linear) */
  background: string;
  /** 巫女まわりの光彩色 */
  glow: string;
  /** BGM 用のベース周波数 (Hz) — Web Audio で sine ドローン */
  baseHz: number;
  /** BGM 用倍音オフセット */
  partialHz: number;
  /** メインテキスト色トーン */
  ink: string;
  /** ラベル (ja/en) */
  label: { ja: string; en: string };
};

export const MOOD: Record<TimeOfDay, MoodTheme> = {
  morning: {
    background:
      "radial-gradient(ellipse at center 80%, rgba(255,200,180,0.25), transparent 60%), linear-gradient(to bottom, #2a1a1f 0%, #4a2a2a 40%, #6b3a3a 100%)",
    glow: "rgba(255,200,170,0.45)",
    baseHz: 220,
    partialHz: 330,
    ink: "#faf6ee",
    label: { ja: "朝", en: "Morning" },
  },
  day: {
    background:
      "radial-gradient(ellipse at center 70%, rgba(255,235,200,0.18), transparent 60%), linear-gradient(to bottom, #6b8aa0 0%, #4a6b80 50%, #2a4a60 100%)",
    glow: "rgba(255,240,210,0.4)",
    baseHz: 261,
    partialHz: 392,
    ink: "#faf6ee",
    label: { ja: "昼", en: "Day" },
  },
  dusk: {
    background:
      "radial-gradient(ellipse at center 80%, rgba(255,140,80,0.3), transparent 60%), linear-gradient(to bottom, #4a1a2a 0%, #6b1d2a 40%, #8a3a3a 100%)",
    glow: "rgba(255,180,120,0.55)",
    baseHz: 196,
    partialHz: 294,
    ink: "#faf6ee",
    label: { ja: "夕", en: "Dusk" },
  },
  night: {
    background:
      "radial-gradient(ellipse at center 70%, rgba(159,29,53,0.18), transparent 60%), linear-gradient(to bottom, #050203 0%, #0a0608 50%, #1a0c12 100%)",
    glow: "rgba(201,169,97,0.4)",
    baseHz: 174,
    partialHz: 261,
    ink: "#faf6ee",
    label: { ja: "夜", en: "Night" },
  },
};

/**
 * 巫女のセリフを過去ドローと時刻から動的生成。
 * Phase 1 はテンプレ抽選 (Track B のクラウド生成が来たら fetch に差し替え)。
 */
export function pickHomeMessage(opts: {
  locale: "ja" | "en";
  timeOfDay: TimeOfDay;
  lastDraw?: StoredDraw;
  pendingPartnerName?: string;
  daysSinceLastDraw?: number;
}): string {
  const { locale, timeOfDay, lastDraw, pendingPartnerName, daysSinceLastDraw } =
    opts;

  // 1. 相手からの誘いが来ている場合 (最優先)
  if (pendingPartnerName) {
    return locale === "en"
      ? `${pendingPartnerName} is waiting at the shrine for you.`
      : `${pendingPartnerName} さんが境内であなたを待っています。`;
  }

  // 2. 久しぶりの来訪
  if (daysSinceLastDraw !== undefined && daysSinceLastDraw >= 3) {
    return locale === "en"
      ? "It has been a while. The lanterns brightened upon your return."
      : "ひさしぶりに来てくれましたね。灯がふっと明るくなりました。";
  }

  // 3. 直近のドローを思い出させる
  if (lastDraw) {
    const rd = RARITY_DISPLAY[lastDraw.rarity];
    if (
      lastDraw.rarity === "tokudai_kichi" ||
      lastDraw.rarity === "dai_kichi"
    ) {
      return locale === "en"
        ? `That ${rd.en.toLowerCase()} from the other day still glows in the air.`
        : `先日の「${rd.ja}」、まだ境内に光が残っております。`;
    }
    if (lastDraw.rarity === "kyo" || lastDraw.rarity === "dai_kyo") {
      return locale === "en"
        ? "The wind has shifted since last time. Do not fear."
        : "あの日とは風向きが変わりましたよ。怖るることなかれ。";
    }
  }

  // 4. 時刻別の通常セリフ
  const dict: Record<TimeOfDay, { ja: string; en: string }> = {
    morning: {
      ja: "朝の鈴が鳴りました。今日もよく来てくれましたね。",
      en: "The morning bell has rung. Glad to see you today.",
    },
    day: {
      ja: "風が穏やかです。札を引きましょうか。",
      en: "The wind is gentle. Shall we draw a slip?",
    },
    dusk: {
      ja: "夕焼けが鳥居を染めています。少しお話しませんか。",
      en: "The sunset paints the torii. Stay a while?",
    },
    night: {
      ja: "夜風が冷たいですね。心の問いを聞かせてください。",
      en: "The night wind is cool. Tell me what your heart asks.",
    },
  };
  return dict[timeOfDay][locale];
}

/**
 * 巫女タップ時の小ボイス (placeholder)。
 * Web Audio で「ふふ」風の短い和音を鳴らすためのトーンセット。
 * useSound からこの関数を呼んで oscillator を組み立てる。
 */
export const MIKO_VOICE_NOTES: number[][] = [
  [392, 523, 659], // G C E
  [349, 440, 587], // F A D
  [330, 494, 659], // E B E
  [440, 587, 740], // A D F#
];

export function pickMikoVoiceChord(): number[] {
  return MIKO_VOICE_NOTES[Math.floor(Math.random() * MIKO_VOICE_NOTES.length)];
}

/**
 * 経過日数 (今日との差) を返す。
 */
export function daysBetween(
  isoDate: string | undefined,
  today: Date = new Date(),
): number | undefined {
  if (!isoDate) return undefined;
  const d = new Date(isoDate);
  const t = new Date(today);
  d.setHours(0, 0, 0, 0);
  t.setHours(0, 0, 0, 0);
  return Math.round((t.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
}
